/**
 * **文档库落在哪儿** —— 一个很小的存储适配层（IndexedDB 优先，localStorage 兜底）。
 *
 * ## 为什么要换成 IndexedDB（用户原话）
 *
 * 第一版整份库存在 `localStorage`（约 5MB），而**图片是 base64 内嵌进文档的**
 * （见 `types/widget.ts` 的 `ImageWidget`）—— 几张照片就能把配额撑爆。
 * 用户选了"两个都做"，所以现在：
 *
 * ```text
 *   indexedDB 可用 → IndexedDB（整份库存一条记录；容量按磁盘算，不是 5MB）
 *   不可用 / 打不开 → localStorage（老路，配额满会明确报错）
 *   都没有         → 内存（本次会话能用，刷新就没 —— 但**绝不静默丢失**：
 *                    界面上会写清"这次改动没能存下来"）
 * ```
 *
 * ## ⚠️ 迁移：老用户的文档不会消失
 *
 * 首次用 IndexedDB 打开时，如果 **IDB 里是空的** 而 localStorage 里还有
 * 老那份库（`node-print.documents.v1`），就把整份搬过去，然后**删掉老键**。
 * 不删的话下次又会搬一遍（用户在 IDB 里删掉的文档会"复活"）。
 *
 * ## ⚠️ 为什么 `save` 是"整份覆盖"而不是逐条写
 *
 * 复用同一套纯逻辑（`parseLibrary` / `serializeLibrary` 那份形状），
 * 也让"库"的真相始终只有一份。文档数量是几十份量级、每份几十 KB ——
 * 整份写的代价可以接受，换来的是**不会出现"一半新一半旧"的库**。
 */
import {
  LIBRARY_KEY,
  emptyLibrary,
  parseLibrary,
  readLibrary,
  writeLibrary,
  type LibraryState,
  type LibraryStorage,
  type WriteResult,
} from './documentLibrary'

export type StoreKind = 'indexeddb' | 'localstorage' | 'memory'

export interface DocumentStore {
  /** 界面上要写清"存哪儿"（用户得知道刷新之后还在不在） */
  readonly kind: StoreKind
  /** 读整份库 —— **读不出来给空库**（不是抛错：存储坏了不该让编辑器起不来） */
  load(): Promise<LibraryState>
  /** 存整份库 */
  save(state: LibraryState): Promise<WriteResult>
}

/** 内存 store：测试用，也是"什么存储都没有"时的最后兜底 */
export function createMemoryStore(initial: LibraryState = emptyLibrary()): DocumentStore {
  let state = initial
  return {
    kind: 'memory',
    async load() {
      return state
    },
    async save(next) {
      state = next
      return { ok: true }
    },
  }
}

/** localStorage store：第一版那条路，留着当兜底 */
export function createLocalStorageStore(storage: LibraryStorage | undefined): DocumentStore {
  return {
    kind: 'localstorage',
    async load() {
      return readLibrary(storage)
    },
    async save(state) {
      return writeLibrary(state, storage)
    },
  }
}

const DB_NAME = 'node-print'
const DB_VERSION = 1
const STORE_NAME = 'library'
/** 整份库存在这条记录上（见文件头"整份覆盖"那段） */
const RECORD_KEY = 'current'

/** IndexedDB 的最小形状（避免为了几个方法去依赖 lib.dom 的完整类型） */
interface IdbLike {
  open(name: string, version: number): IDBOpenDBRequest
}

function idb(): IdbLike | undefined {
  return (globalThis as { indexedDB?: IdbLike }).indexedDB
}

/** `indexedDB` 到底能不能用（有些环境有它但一打开就抛：隐私模式 / 被策略禁用） */
export function indexedDbAvailable(): boolean {
  return typeof idb()?.open === 'function'
}

function request<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error ?? new Error('IndexedDB 请求失败'))
  })
}

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = idb()!.open(DB_NAME, DB_VERSION)
    req.onupgradeneeded = () => {
      const db = req.result
      if (!db.objectStoreNames.contains(STORE_NAME)) db.createObjectStore(STORE_NAME)
    }
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error ?? new Error('打不开 IndexedDB'))
    /* 被另一个标签页占着升级时会走这条（罕见，但要给出路，不能永远挂着） */
    req.onblocked = () => reject(new Error('IndexedDB 被另一个标签页占着'))
  })
}

/**
 * IndexedDB store。
 *
 * ⚠️ **打开失败会退回落地的 store**（隐私模式 / 策略禁用 / 被占用都算）——
 * 返回一个"永远好用"的 store，调用方不必到处 try。
 * 唯一的代价是容量回到 5MB，而这件事通过 `kind` 告诉界面。
 */
export async function createIndexedDbStore(
  fallback: DocumentStore,
  /** 迁移源：老那份 localStorage 的键（默认就是第一版那个键） */
  legacy?: LibraryStorage,
): Promise<DocumentStore> {
  if (!indexedDbAvailable()) return fallback
  let db: IDBDatabase
  try {
    db = await openDatabase()
  } catch {
    return fallback
  }

  const readAll = async (): Promise<LibraryState | null> => {
    const tx = db.transaction(STORE_NAME, 'readonly')
    const value = await request(tx.objectStore(STORE_NAME).get(RECORD_KEY))
    if (value === undefined || value === null) return null
    /* 存的是对象本身（结构化克隆），过一遍 parseLibrary 做一次**同口径**的容错 */
    return parseLibrary(JSON.stringify(value))
  }

  const writeAll = (state: LibraryState): Promise<WriteResult> =>
    new Promise((resolve) => {
      try {
        const tx = db.transaction(STORE_NAME, 'readwrite')
        tx.objectStore(STORE_NAME).put(
          /* 只存纯数据（结构化克隆不吃函数/代理） */
          JSON.parse(JSON.stringify(state)) as LibraryState,
          RECORD_KEY,
        )
        tx.oncomplete = () => resolve({ ok: true })
        tx.onerror = () =>
          resolve({ ok: false, message: `存不进 IndexedDB：${tx.error?.message ?? '未知原因'}` })
        tx.onabort = () =>
          resolve({ ok: false, message: `存不进 IndexedDB：${tx.error?.message ?? '事务被中止'}` })
      } catch (error) {
        resolve({ ok: false, message: `存不进 IndexedDB：${(error as Error).message}` })
      }
    })

  return {
    kind: 'indexeddb',
    async load() {
      const current = await readAll()
      if (current) return current
      /*
       * ★ 迁移：IDB 里还没有东西，而老那份 localStorage 库有 —— 搬过来。
       * 搬完**删掉老键**：不删的话，用户在 IDB 里删掉的文档会在下次
       * "再搬一遍"里复活。
       */
      const legacyState = readLibrary(legacy)
      if (legacyState.documents.length === 0) return current ?? emptyLibrary()
      await writeAll(legacyState)
      try {
        legacy?.setItem(LIBRARY_KEY, '')
      } catch {
        /* 删不掉也无所谓（下次读到空库，不会再搬） */
      }
      return legacyState
    },
    save: writeAll,
  }
}

/**
 * 挑一个能用的 store（**这是调用方唯一该用的入口**）。
 *
 * ⚠️ 是 `async` 的：IndexedDB 要先开库。宿主因此要在启动时 `await` 一下
 * （`useDocuments` 里那个 `ready`）。
 */
export async function createBestStore(
  legacy: LibraryStorage | undefined = (globalThis as { localStorage?: LibraryStorage }).localStorage,
): Promise<DocumentStore> {
  const fallback = legacy ? createLocalStorageStore(legacy) : createMemoryStore()
  return createIndexedDbStore(fallback, legacy)
}
